/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import { toaster } from "baseui/toast";
import { AxiosInstance } from "axios";
import { LogLevel, Component } from "../Api/Api";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaInfoCircle,
} from "react-icons/fa";
import React from "react";

export interface __INotify__ {
  positive(message: string): void;
  warning(message: string): void;
  negative(message: string): void;
  info(message: string): void;
}

const UNIMPORTANT_MESSAGE_DURATION_MS = 4000;
const UNIMPORTANT_MESSAGE_PROPS = {
  autoHideDuration: UNIMPORTANT_MESSAGE_DURATION_MS,
};

/**
 * Wraps a toast message with a shape indicator icon.
 *
 * MIL-STD-1472H Section 5.17 requires that color-coded status information
 * be supplemented with a secondary differentiator (shape, text, or pattern)
 * so that the state is identifiable independent of color perception.
 *
 * The icon is marked aria-hidden because the toast text already communicates
 * the meaning — screen readers should read the message, not the icon name.
 */
function withShapeIndicator(
  IconComponent: React.ElementType,
  message: string
): React.ReactNode {
  return React.createElement(
    "span",
    {
      style: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        minWidth: 0,
      },
    },
    React.createElement(IconComponent, {
      size: 16,
      "aria-hidden": true,
      style: { flexShrink: 0 },
    }),
    React.createElement(
      "span",
      { style: { minWidth: 0, overflowWrap: "anywhere" } },
      message
    )
  );
}

export const notify: __INotify__ = {
  positive(message: string): void {
    toaster.positive(
      withShapeIndicator(FaCheckCircle, message),
      UNIMPORTANT_MESSAGE_PROPS
    );
  },
  warning(message: string): void {
    toaster.warning(withShapeIndicator(FaExclamationTriangle, message), {});
  },
  negative(message: string): void {
    toaster.negative(withShapeIndicator(FaTimesCircle, message), {});
  },
  info(message: string): void {
    if (message.trim() !== "") {
      toaster.info(
        withShapeIndicator(FaInfoCircle, message),
        UNIMPORTANT_MESSAGE_PROPS
      );
    }
  },
};

// Re-export Component enum for convenience
export { Component } from "../Api/Api";

// Backend logging function
async function logToBackend(
  axios: AxiosInstance,
  level: LogLevel,
  component: Component,
  message: string
): Promise<void> {
  try {
    await axios.post("/addEventLog", {
      level,
      component: component.toString(), // Convert enum to string for backend
      message,
    });
  } catch (error) {
    // Silently fail - don't break the UI if logging fails
    console.error("Failed to log event to backend:", error);
  }
}

// Enhanced notify interface that logs to backend
export interface __ILoggedNotify__ {
  positive(axios: AxiosInstance, component: Component, message: string): void;
  warning(axios: AxiosInstance, component: Component, message: string): void;
  negative(axios: AxiosInstance, component: Component, message: string): void;
  info(axios: AxiosInstance, component: Component, message: string): void;
}

/**
 * Notification system with backend logging.
 * Shows toast notification AND logs to MongoDB via backend endpoint.
 *
 * Usage:
 * ```
 * const axiosContext = React.useContext(AxiosContext);
 * loggedNotify.negative(axiosContext, Component.FILE_UPLOAD, "Upload failed");
 * ```
 *
 * Available components:
 * - Component.FILE_UPLOAD - File uploader
 * - Component.TRANSFORM_ENGINE - Transform engine
 * - Component.QUICKFORM - QuickForm
 * - Component.INPUTS - Inputs panel
 * - Component.FILES - Files panel
 */
export const loggedNotify: __ILoggedNotify__ = {
  positive(axios: AxiosInstance, component: Component, message: string): void {
    notify.positive(message);
    logToBackend(axios, LogLevel.INFORMATION, component, message);
  },
  warning(axios: AxiosInstance, component: Component, message: string): void {
    notify.warning(message);
    logToBackend(axios, LogLevel.WARNING, component, message);
  },
  negative(axios: AxiosInstance, component: Component, message: string): void {
    notify.negative(message);
    logToBackend(axios, LogLevel.ERROR, component, message);
  },
  info(axios: AxiosInstance, component: Component, message: string): void {
    notify.info(message);
    if (message.trim() !== "") {
      logToBackend(axios, LogLevel.INFORMATION, component, message);
    }
  },
};

/**
 * Batch response notification with backend logging.
 * Use this variant when you want batch failures logged to the backend.
 */
export function loggedNotifyBatchResponse<__TFailed__>(
  axios: AxiosInstance,
  component: Component,
  failedElements: __TFailed__[] | null,
  getAllFailedMessage: (failedElements?: __TFailed__[]) => string
): void {
  const numFailed = failedElements ? failedElements.length : 0;
  if (numFailed > 0) {
    const allFailedMessage = getAllFailedMessage(failedElements);
    loggedNotify.negative(axios, component, allFailedMessage);
  }
}
